import React, { useState, useEffect, useCallback } from "react";
import { supabaseAdmin } from "../lib/supabase";
import ConfirmModal from "../components/ConfirmModal";
import styles from "./TablePage.module.css";

type Type = "infra_server" | "ai_api" | "labor" | "etc";

type Expense = {
  id: string;
  title: string;
  type: Type;
  amount: number;
  currency: string;
  paid_at: string;
  billing_month: string | null;
  start_date: string | null;
  end_date: string | null;
  memo: string | null;
  created_by: string | null;
  created_at: string;
};

const TYPE_LABEL: Record<Type, string> = {
  infra_server: "서버/인프라",
  ai_api: "AI API",
  labor: "인건비",
  etc: "기타",
};

const TYPE_COLOR: Record<Type, string> = {
  infra_server: "#67E8F9",
  ai_api: "#A78BFA",
  labor: "#FFB84D",
  etc: "rgba(255,255,255,0.5)",
};

const EMPTY = {
  title: "",
  type: "infra_server" as Type,
  amount: "",
  currency: "KRW",
  paid_at: new Date().toISOString().split("T")[0],
  billing_month: new Date().toISOString().slice(0, 7),
  start_date: "",
  end_date: "",
  memo: "",
};

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: "5px 14px",
    borderRadius: 999,
    border: "none",
    background: active ? "rgba(255,255,255,0.22)" : "transparent",
    color: active ? "white" : "rgba(255,255,255,0.5)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.15s",
  };
}

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [filterType, setFilterType] = useState<Type | "all">("all");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState("");
  const [form, setForm] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);

  useEffect(() => {
    supabaseAdmin.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    let query = supabaseAdmin
      .from("expenses")
      .select("*")
      .order("paid_at", { ascending: false });

    if (filterType !== "all") query = query.eq("type", filterType);
    if (filterMonth) query = query.eq("billing_month", filterMonth);
    else if (filterYear) query = query.gte("paid_at", `${filterYear}-01-01`).lte("paid_at", `${filterYear}-12-31`);

    const { data, error } = await query;
    if (error) console.warn(error.message);
    setExpenses(data ?? []);
    setLoading(false);
  }, [filterType, filterMonth, filterYear]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Summary
  const totalKRW = expenses
    .filter((e) => e.currency === "KRW")
    .reduce((s, e) => s + Number(e.amount), 0);
  const totalUSD = expenses
    .filter((e) => e.currency === "USD")
    .reduce((s, e) => s + Number(e.amount), 0);

  function openCreate() {
    setForm({ ...EMPTY });
    setCreating(true);
  }

  function openEdit(e: Expense) {
    setForm({
      ...e,
      amount: String(e.amount),
      paid_at: e.paid_at.slice(0, 10),
      billing_month: e.billing_month ?? "",
      start_date: e.start_date ?? "",
      end_date: e.end_date ?? "",
    });
    setCreating(false);
  }

  async function handleSave() {
    if (!form.title || !form.amount || !form.paid_at) {
      alert("제목, 금액, 날짜는 필수입니다.");
      return;
    }
    const payload = {
      title: form.title,
      type: form.type,
      amount: parseFloat(form.amount),
      currency: form.currency || "KRW",
      paid_at: form.paid_at,
      billing_month: form.billing_month || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      memo: form.memo || null,
    };

    if (creating) {
      const { error } = await supabaseAdmin.from("expenses").insert([
        {
          ...payload,
          created_by: currentUser?.id ?? null,
        },
      ]);
      if (error) {
        alert(error.message);
        return;
      }
    } else {
      const { error } = await supabaseAdmin
        .from("expenses")
        .update(payload)
        .eq("id", form.id);
      if (error) {
        alert(error.message);
        return;
      }
    }
    setForm(null);
    fetchExpenses();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await supabaseAdmin.from("expenses").delete().eq("id", deleteTarget.id);
    setDeleteTarget(null);
    fetchExpenses();
  }

  // Unique billing months for filter
  const months = Array.from(
    new Set(expenses.map((e) => e.billing_month).filter(Boolean)),
  )
    .sort()
    .reverse();

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>💸 운영 비용</h1>
          <span className={styles.count}>총 {expenses.length}건</span>
          <div className={styles.dbBadge}>🗄 expenses</div>
        </div>
        <div className={styles.actions}>
          <button className="btn btn-ghost" onClick={fetchExpenses}>
            ↺
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            + 비용 추가
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div
        style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}
      >
        {Object.entries(TYPE_LABEL).map(([type, label]) => {
          const items = expenses.filter((e) => e.type === type);
          const sum = items.reduce(
            (s, e) => s + (e.currency === "KRW" ? Number(e.amount) : 0),
            0,
          );
          return (
            <div
              key={type}
              style={{
                background: "rgba(255,255,255,0.07)",
                borderRadius: 14,
                border: `1px solid ${TYPE_COLOR[type as Type]}44`,
                padding: "14px 20px",
                minWidth: 150,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: TYPE_COLOR[type as Type],
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                {label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "white" }}>
                {sum > 0 ? (
                  `₩${sum.toLocaleString()}`
                ) : (
                  <span style={{ opacity: 0.3 }}>—</span>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.35)",
                  marginTop: 2,
                }}
              >
                {items.length}건
              </div>
            </div>
          );
        })}
        {/* Total */}
        <div
          style={{
            background: "rgba(167,139,250,0.12)",
            borderRadius: 14,
            border: "1px solid rgba(167,139,250,0.3)",
            padding: "14px 20px",
            minWidth: 150,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: "#A78BFA",
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            합계
          </div>
          {totalKRW > 0 && (
            <div style={{ fontSize: 18, fontWeight: 900, color: "white" }}>
              ₩{totalKRW.toLocaleString()}
            </div>
          )}
          {totalUSD > 0 && (
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              ${totalUSD.toFixed(2)}
            </div>
          )}
          {totalKRW === 0 && totalUSD === 0 && (
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: "rgba(255,255,255,0.3)",
              }}
            >
              —
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            background: "rgba(255,255,255,0.1)",
            borderRadius: 999,
            padding: 3,
            gap: 2,
          }}
        >
          {(["all", "infra_server", "ai_api", "labor", "etc"] as const).map(
            (t) => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                style={chipStyle(filterType === t)}
              >
                {t === "all" ? "전체" : TYPE_LABEL[t]}
              </button>
            ),
          )}
        </div>
        <select
          value={filterYear}
          onChange={(e) => { setFilterYear(e.target.value); setFilterMonth(""); }}
          className="input"
          style={{ width: 100, padding: "6px 12px", fontSize: 13 }}
        >
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
            <option key={y} value={String(y)}>{y}년</option>
          ))}
        </select>
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="input"
          style={{ width: 140, padding: "6px 12px", fontSize: 13 }}
        >
          <option value="">전체 월</option>
          {months.map((m) => (
            <option key={m} value={m!}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* List */}
      {expenses.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>💸</div>
          <div>비용 내역이 없습니다</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {expenses.map((e, i) => (
            <div
              key={e.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "rgba(255,255,255,0.07)",
                border: `1px solid ${TYPE_COLOR[e.type]}33`,
                borderRadius: 12,
                padding: "14px 18px",
              }}
            >
              {/* Type */}
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 1,
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: `${TYPE_COLOR[e.type]}22`,
                  color: TYPE_COLOR[e.type],
                  flexShrink: 0,
                }}
              >
                {TYPE_LABEL[e.type]}
              </span>

              {/* Title */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "white",
                    marginBottom: e.memo ? 3 : 0,
                  }}
                >
                  {e.title}
                </div>
                {e.memo && (
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                    {e.memo}
                  </div>
                )}
              </div>

              {/* Billing month / 구독 기간 */}
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", flexShrink: 0, textAlign: 'right' }}>
                {e.start_date && e.end_date
                  ? <span style={{ color: '#67E8F9' }}>{e.start_date.slice(0, 10)} ~ {e.end_date.slice(0, 10)}</span>
                  : e.billing_month || null}
              </span>

              {/* Amount */}
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "white",
                  flexShrink: 0,
                  minWidth: 100,
                  textAlign: "right",
                }}
              >
                {e.currency === "KRW" ? "₩" : "$"}
                {Number(e.amount).toLocaleString()}
              </span>

              {/* Date */}
              <span
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.35)",
                  flexShrink: 0,
                  width: 70,
                  textAlign: "right",
                }}
              >
                {e.paid_at.slice(0, 10)}
              </span>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 11 }}
                  onClick={() => openEdit(e)}
                >
                  편집
                </button>
                <button
                  className="btn btn-danger"
                  style={{ padding: "4px 10px", fontSize: 11 }}
                  onClick={() => setDeleteTarget(e)}
                >
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {form && (
        <div className="modal-overlay" onClick={() => setForm(null)}>
          <div
            className="modal-box"
            style={{ width: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <h2 style={{ fontSize: 18, fontWeight: 800 }}>
                {creating ? "비용 추가" : "비용 편집"}
              </h2>
              <button
                className="btn btn-ghost"
                style={{ padding: "4px 8px" }}
                onClick={() => setForm(null)}
              >
                ✕
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={labelStyle}>제목</label>
                <input
                  className="input"
                  placeholder="예: Supabase Pro 4월"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p: any) => ({ ...p, title: e.target.value }))
                  }
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>유형</label>
                  <select
                    className="input"
                    value={form.type}
                    onChange={(e) =>
                      setForm((p: any) => ({ ...p, type: e.target.value }))
                    }
                  >
                    {Object.entries(TYPE_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>통화</label>
                  <select
                    className="input"
                    value={form.currency}
                    onChange={(e) =>
                      setForm((p: any) => ({ ...p, currency: e.target.value }))
                    }
                  >
                    <option value="KRW">KRW (₩)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>금액</label>
                  <input
                    className="input"
                    type="number"
                    placeholder="0"
                    value={form.amount}
                    onChange={(e) =>
                      setForm((p: any) => ({ ...p, amount: e.target.value }))
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>결제일</label>
                  <input
                    className="input"
                    type="date"
                    value={form.paid_at}
                    onChange={(e) =>
                      setForm((p: any) => ({ ...p, paid_at: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>청구 월 (선택)</label>
                <input
                  className="input"
                  type="month"
                  value={form.billing_month ?? ""}
                  onChange={(e) =>
                    setForm((p: any) => ({
                      ...p,
                      billing_month: e.target.value,
                    }))
                  }
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>구독 시작일 (선택)</label>
                  <input className="input" type="date" value={form.start_date ?? ""}
                    onChange={(e) => setForm((p: any) => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>구독 종료일 (선택)</label>
                  <input className="input" type="date" value={form.end_date ?? ""}
                    onChange={(e) => setForm((p: any) => ({ ...p, end_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>메모 (선택)</label>
                <textarea
                  className="input"
                  rows={2}
                  style={{ resize: "vertical" }}
                  value={form.memo ?? ""}
                  onChange={(e) =>
                    setForm((p: any) => ({ ...p, memo: e.target.value }))
                  }
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 24,
                justifyContent: "flex-end",
              }}
            >
              <button className="btn btn-ghost" onClick={() => setForm(null)}>
                취소
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {creating ? "추가" : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="비용 삭제"
          message={`"${deleteTarget.title}" 항목을 삭제할까요?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "rgba(255,255,255,0.6)",
  letterSpacing: 1,
  display: "block",
  marginBottom: 6,
};
